import { Link } from 'react-router-dom';
import type { RoleDetailResponse, RoleListResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import styles from './RolesPage.module.css';

/**
 * Roles — the role library (docs/UX_ARCHITECTURE.md §2, MVP).
 *
 * What roles exist, and what each one requires. Nothing about any person
 * appears here, deliberately: `docs/ROLE_FRAMEWORK.md` §5 draws the line
 * between "how does employee X fit role Y" — a contextual question with an
 * answer — and ranking people, which this product does not do. A library
 * that listed candidates beside each role would cross that line, and
 * `docs/MVP_SCOPE.md` §6 rules cross-employee ranking out of the MVP.
 *
 * Roles are also not ranked against each other. `levelRank` is displayed
 * where the role records one, because it is a stored field and it is how an
 * organisation describes its own structure — but nothing here sorts roles
 * into better and worse, and no seniority is inferred from a title. The
 * ordering is the API's own (department, then level, then name), so the
 * page does not decide it either.
 */
export function RolesPage() {
  const state = useApiResource<RoleListResponse>('/roles');

  if (state.status === 'loading') return <LoadingState label="Loading roles…" />;

  if (state.status === 'failed') {
    return <ErrorState title="The role library could not be loaded" detail={describeError(state.error)} />;
  }

  const { roles, count } = state.data;

  // Grouping only — the API decided the order, and this preserves it.
  const byDepartment = new Map<string, RoleDetailResponse[]>();
  for (const role of roles) {
    const key = role.departmentName ?? 'No department recorded';
    const bucket = byDepartment.get(key);
    if (bucket) bucket.push(role);
    else byDepartment.set(key, [role]);
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Roles</h1>
        <p className={styles.scope}>
          {count} {count === 1 ? 'role' : 'roles'} defined
        </p>
      </header>

      <p className={styles.note}>
        The roles this organisation defines, and what each one requires. A role is a definition, not a person — no
        employee information appears here.
      </p>

      {roles.length === 0 ? (
        <EmptyState
          title="No roles are defined"
          detail="The role library is empty, so there is nothing to compare anyone against yet."
        />
      ) : (
        [...byDepartment.entries()].map(([departmentName, departmentRoles]) => (
          <section key={departmentName} className={styles.group} aria-label={departmentName}>
            <h2 className={styles.groupHeading}>{departmentName}</h2>
            <ul className={styles.list}>
              {departmentRoles.map((role) => (
                <li key={role.id}>
                  <Link to={`/roles/${role.id}`} className={styles.card}>
                    <span className={styles.name}>{role.name}</span>
                    <span className={styles.level}>
                      {role.levelLabel ?? <span className={styles.absent}>No level recorded</span>}
                    </span>
                    <span className={styles.meta}>
                      {/* A retired role is listed rather than hidden: historical
                          assignments still reference it. It is labelled so it is
                          never mistaken for one someone could be assigned today. */}
                      {role.status === 'retired' ? <span className={styles.retired}>Retired</span> : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
